import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { useApiErrorHandler } from '../../../useApiErrorHandler'

const deleteGhostProfile = async (ghostProfileId: string): Promise<void> => {
  await api.delete(`/ghost-profiles/${ghostProfileId}`)
}

// Everything the server deletes with the profile, or that lists the person.
// Not ['profile', id]: its screen is still open and would ask for a deleted
// profile.
const LISTS_TO_REFRESH = [
  'contacts',
  'notes',
  'keep-contacts',
  'categories',
  'hashtags',
  'category-contacts',
  'hashtag-contacts',
  'color-label-contacts',
  'birthdays',
  'notifications',
]

export const useDeleteGhostProfile = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const handleApiError = useApiErrorHandler()
  return useMutation<void, Error, string>({
    mutationFn: deleteGhostProfile,
    onSuccess: async () => {
      showSuccessToast(t('toast.success'), t('ghostProfile.deleteSuccess'))
      await Promise.all([
        ...LISTS_TO_REFRESH.map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
        // Marked stale only: a screen still in memory (the note editor, the
        // calendar) may hold the deleted person's card, and asking for it
        // again answers 404 and retries for seconds before the screen closes.
        queryClient.invalidateQueries({ queryKey: ['contact'], refetchType: 'none' }),
      ])
    },
    // A refused deletion says so instead of failing in silence.
    onError: handleApiError,
  })
}
